/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * ServiceControlledFeatureState — runtime + persisted state for
 * SERVICE_CONTROLLED features.
 *
 * `isFeatureEnabled()` is synchronous, but IndexedDB is async, so this holds an
 * in-memory `Map<Feature, boolean>` that is hydrated from IndexedDB once at
 * bootstrap (before render) and read synchronously thereafter. Whenever a fresh
 * source value arrives, it is written to memory and persisted, so the persisted
 * value is always the latest known valid state and can serve as a fallback on
 * the next launch.
 *
 * This is a thin cache over the existing `StorageAdapter` (the project's single
 * IndexedDB abstraction) — no new storage layer, no direct IndexedDB access
 * from `FeatureActivation`. Feature Overrides are deliberately NOT stored here.
 */

import { StorageAdapter } from "../storage";
import { Log } from "../logging/Log";
import { registeredFeatures } from "./registry";
import type { Feature } from "./types";

const TAG: string = "ServiceControlledFeatureState";

/** Single IndexedDB key holding the persisted `{ [feature]: boolean }` map. */
export const SERVICE_CONTROLLED_STATE_STORAGE_KEY: string =
  "featureActivation.serviceState";

/** Retain the fallback for a long time — it is refreshed on every fresh value. */
const STATE_TTL_MS: number = 365 * 24 * 60 * 60 * 1000;

const KNOWN_FEATURES: ReadonlySet<string> = new Set<string>(registeredFeatures);

export class ServiceControlledFeatureState {
  private static mInstance: ServiceControlledFeatureState | null = null;

  private mState: Map<Feature, boolean> = new Map<Feature, boolean>();

  private constructor() {}

  public static getInstance(): ServiceControlledFeatureState {
    if (ServiceControlledFeatureState.mInstance === null) {
      ServiceControlledFeatureState.mInstance =
        new ServiceControlledFeatureState();
    }
    return ServiceControlledFeatureState.mInstance;
  }

  /** Test seam — drops the singleton and its in-memory state. */
  public static resetInstance(): void {
    ServiceControlledFeatureState.mInstance = null;
  }

  /**
   * Loads the persisted map into memory. Called once at bootstrap after
   * `StorageAdapter.init()`. Malformed/missing data is treated as empty; a
   * hydration failure leaves the in-memory state empty (fail-closed).
   */
  public async hydrate(): Promise<void> {
    try {
      const persisted = await StorageAdapter.getInstance().get<
        Record<string, boolean>
      >(SERVICE_CONTROLLED_STATE_STORAGE_KEY);
      if (persisted === null || typeof persisted !== "object") {
        return;
      }
      const keys: Array<string> = Object.keys(persisted);
      for (let i = 0; i < keys.length; i += 1) {
        const key: string = keys[i];
        const value: unknown = persisted[key];
        if (KNOWN_FEATURES.has(key) && typeof value === "boolean") {
          this.mState.set(key as Feature, value);
        }
      }
    } catch (error: unknown) {
      Log.w(TAG, "hydrate() — could not read persisted service state", error);
    }
  }

  /** Latest known state for a feature, or `null` when none is known. */
  public getState(feature: Feature): boolean | null {
    const value = this.mState.get(feature);
    return value === undefined ? null : value;
  }

  /**
   * Records the latest known valid state and persists it. A no-op when the
   * value is unchanged, so synchronous reads do not trigger redundant writes.
   */
  public setState(feature: Feature, enabled: boolean): void {
    if (this.mState.get(feature) === enabled) {
      return;
    }
    this.mState.set(feature, enabled);
    this.persist();
  }

  /** Clears all runtime and persisted service state (e.g. on sign-out). */
  public clear(): void {
    this.mState = new Map<Feature, boolean>();
    try {
      StorageAdapter.getInstance()
        .delete(SERVICE_CONTROLLED_STATE_STORAGE_KEY)
        .catch((error: unknown): void => {
          Log.w(TAG, "clear() — could not delete persisted state", error);
        });
    } catch (error: unknown) {
      Log.w(TAG, "clear() — storage unavailable", error);
    }
  }

  private persist(): void {
    const snapshot: Record<string, boolean> = {};
    this.mState.forEach((value: boolean, key: Feature): void => {
      snapshot[key] = value;
    });
    try {
      StorageAdapter.getInstance()
        .set(SERVICE_CONTROLLED_STATE_STORAGE_KEY, snapshot, STATE_TTL_MS)
        .catch((error: unknown): void => {
          Log.w(TAG, "persist() — could not write service state", error);
        });
    } catch (error: unknown) {
      // Storage not initialised (e.g. in unit tests) — memory state still holds.
      Log.w(TAG, "persist() — storage unavailable", error);
    }
  }
}
